import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface SnipRectPx {
  left: number;
  top: number;
  width: number;
  height: number;
}

@Component({
  selector: 'app-bbox-snip-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bbox-snip-overlay.component.html',
  styleUrl: './bbox-snip-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BboxSnipOverlayComponent implements OnInit, OnDestroy {
  @Input() attributeLabel: string = '';

  @Output() boxDrawn = new EventEmitter<SnipRectPx>();
  @Output() cancelled = new EventEmitter<void>();

  isDragging = false;
  selection: SnipRectPx | null = null;

  private startX = 0;
  private startY = 0;
  private boundEscape = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelled.emit();
    }
  };

  ngOnInit(): void {
    document.addEventListener('keydown', this.boundEscape);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.boundEscape);
  }

  onPointerDown(event: PointerEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.startX = event.clientX - rect.left;
    this.startY = event.clientY - rect.top;
    this.selection = { left: this.startX, top: this.startY, width: 0, height: 0 };
    this.isDragging = true;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const curX = event.clientX - rect.left;
    const curY = event.clientY - rect.top;
    this.selection = this.buildRect(this.startX, this.startY, curX, curY);
  }

  onPointerUp(event: PointerEvent): void {
    if (!this.isDragging) {
      return;
    }
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    this.isDragging = false;
    if (!this.selection || this.selection.width < 2 || this.selection.height < 2) {
      this.selection = null;
      return;
    }
    this.boxDrawn.emit({ ...this.selection });
    this.selection = null;
  }

  onCancelClick(event: MouseEvent): void {
    event.stopPropagation();
    this.cancelled.emit();
  }

  private buildRect(sx: number, sy: number, ex: number, ey: number): SnipRectPx {
    return {
      left: Math.min(sx, ex),
      top: Math.min(sy, ey),
      width: Math.abs(ex - sx),
      height: Math.abs(ey - sy),
    };
  }
}
